import React, { useRef, useState } from "react";

// React Router Dom
import { useNavigate } from "react-router-dom";

// React Icons
import { FaEye, FaEyeSlash } from "react-icons/fa";

// Context
import { useAuth } from "../contexts/AuthContext";

// React Hook Form
import { useForm } from "react-hook-form";

// Yup
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// Hooks
import useSnackbarNotification from "../hooks/useSnackbarNotification";

import TurnstileWidget from "../components/TurnstileWidget";

const schema = yup.object().shape({
    email: yup
        .string()
        .email("Please enter a valid email address")
        .matches(
            /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            "Please enter a valid email address"
        )
        .required("Email is required"),
    password: yup
        .string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .required("Confirm password is required"),
    agreeToTerms: yup.boolean().oneOf([true], "You must agree to the terms"),
    confirmMedicalField: yup
        .boolean()
        .oneOf([true], "You must confirm you are in the medical field"),
    receiveJobAlerts: yup.boolean(),
});

const SignUpPage = () => {
    const navigate = useNavigate();
    const { showError, showSuccess } = useSnackbarNotification();
    const { signUp } = useAuth();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const turnstileRef = useRef(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const onSubmit = async (data) => {
        const captchaToken = turnstileRef.current?.getToken() || "";
        const turnstileConfigured = Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY);
        if (!turnstileConfigured && process.env.NODE_ENV === "production") {
            showError("Verification is unavailable. Please try again later.");
            return;
        }
        if (turnstileConfigured && !captchaToken) {
            showError("Please complete the verification check before signing up.");
            return;
        }

        setLoading(true);
        setSuccessMessage("");

        try {
            localStorage.setItem('signupEmail', data.email);
            const { error, data: signUpData } = await signUp(
                data.email,
                data.password,
                {
                    receive_job_alerts: data.receiveJobAlerts || false,
                },
                captchaToken
            );

            // Check if user was created successfully, even if there was an email error
            const userWasCreated = signUpData?.user !== null && signUpData?.user !== undefined;
            const isEmailError = error?.message?.includes('confirmation email') || 
                                error?.message?.includes('Error sending') ||
                                error?.message?.includes('email');

            if (userWasCreated) {
                if (signUpData?.user?.identities?.length === 0) {
                    throw new Error("An account with this email already exists.");
                }

                // User was created successfully - navigate to email verification page
                setLoading(false);
                if (!isEmailError) {
                    showSuccess("Account created successfully! Please check your email for verification.");
                }
                navigate("/email-verification", { 
                    state: { email: data.email } 
                });
                return;
            }

            if (error) {
                turnstileRef.current?.reset();
                if (isEmailError) {
                    showError("There was an issue with email confirmation. Your account may have been created. Please try logging in, or contact support if you continue to have issues.");
                } else {
                    showError(error.message || "An error occurred while creating your account");
                }
                setLoading(false);
                return;
            }

            showSuccess(
                "Your account has been created! Please check your email for verification instructions. If you don't see the email, check your spam/junk folder."
            );
            setSuccessMessage(
                "Your account has been created! Please check your email for verification instructions. If you don't see the email, check your spam/junk folder."
            );
            setLoading(false);
            
            // Store redirect intent in localStorage if redirect parameter is set
            // if (redirectParam === 'mock-interview') {
            //     localStorage.setItem('redirectAfterLogin', 'mock-interview');
            // }
            
            setTimeout(() => {
                // const redirectPath = redirectParam === 'mock-interview' 
                //     ? '/login?redirect=mock-interview' 
                //     : '/login';
                // navigate(redirectPath);
                navigate('/confirm-email');
            }, 5000);
        } catch (err) {
            turnstileRef.current?.reset();
            console.error("Signup error:", err);
            
            // Check if it's an email error and provide helpful guidance
            const isEmailError = err.message?.includes('confirmation email') || 
                                err.message?.includes('Error sending') ||
                                err.message?.includes('email');
            
            if (isEmailError) {
                showError("There was an issue sending the confirmation email. Your account may have been created. Please try logging in, or contact support if you need help.");
            } else {
                showError(err.message || "An error occurred while creating your account");
            }
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
            <div
                className="logo cursor-pointer absolute top-6 left-6"
                onClick={() => navigate("/")}
            >
                Doctors' Jobs <span className="logo-badge">Beta</span>
            </div>
            <div className="sm:mx-auto sm:w-full sm:max-w-3xl">
                <div className="bg-white py-10 px-12 shadow-lg rounded-2xl">
                    <h2 className="text-center text-2xl font-bold text-gray-900 mb-6">
                        Create an account
                    </h2>

                    {successMessage && (
                        <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4">
                            <p className="text-sm text-green-700">
                                {successMessage}
                            </p>
                        </div>
                    )}

                    <form
                        className="space-y-4"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        {/* Email */}
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-sm font-medium text-gray-700 mb-3"
                            >
                                Email Address *
                            </label>
                            <input
                                {...register("email")}
                                id="email"
                                type="email"
                                className={`appearance-none block w-full px-3 py-2 border ${
                                    errors.email
                                        ? "border-red-300"
                                        : "border-gray-300"
                                } rounded-full shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                            />
                            {errors.email && (
                                <p className="mt-1 text-sm text-red-600">
                                    {errors.email.message}
                                </p>
                            )}
                        </div>

                        {/* Password Fields Side by Side */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Password */}
                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-sm font-medium text-gray-700 mb-3"
                                >
                                    Password *
                                </label>
                                <div className="relative">
                                    <input
                                        {...register("password")}
                                        id="password"
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                                            errors.password
                                                ? "border-red-300"
                                                : "border-gray-300"
                                        } rounded-full shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                    />
                                    <button
                                        type="button"
                                        onClick={togglePasswordVisibility}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showPassword ? (
                                            <FaEyeSlash className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <FaEye className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.password.message}
                                    </p>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label
                                    htmlFor="confirmPassword"
                                    className="block text-sm font-medium text-gray-700 mb-3"
                                >
                                    Confirm Password *
                                </label>
                                <div className="relative">
                                    <input
                                        {...register("confirmPassword")}
                                        id="confirmPassword"
                                        type={
                                            showConfirmPassword
                                                ? "text"
                                                : "password"
                                        }
                                        className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                                            errors.confirmPassword
                                                ? "border-red-300"
                                                : "border-gray-300"
                                        } rounded-full shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                    />
                                    <button
                                        type="button"
                                        onClick={
                                            toggleConfirmPasswordVisibility
                                        }
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showConfirmPassword ? (
                                            <FaEyeSlash className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <FaEye className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.confirmPassword.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Checkboxes */}
                        <div className="space-y-6 mt-8">
                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input
                                        {...register("agreeToTerms")}
                                        id="agreeToTerms"
                                        type="checkbox"
                                        className="h-4 w-4 accent-black focus:ring-black border-gray-300 rounded"
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label
                                        htmlFor="agreeToTerms"
                                        className="font-medium text-gray-700"
                                    >
                                        I understand by creating an account, I
                                        agree to Four Minute Medicine's{" "}
                                        <a
                                            href="https://www.fourminutemedicine.com/files/terms-and-conditions.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-black hover:underline underline"
                                        >
                                            Terms of Service
                                        </a>{" "}
                                        and{" "}
                                        <a
                                            href="https://www.fourminutemedicine.com/files/privacy-policy.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-black hover:underline underline"
                                        >
                                            Privacy Policy
                                        </a>
                                        . *
                                    </label>
                                </div>
                            </div>
                            {errors.agreeToTerms && (
                                <p className="mt-1 text-sm text-red-600">
                                    {errors.agreeToTerms.message}
                                </p>
                            )}

                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input
                                        {...register("confirmMedicalField")}
                                        id="confirmMedicalField"
                                        type="checkbox"
                                        className="h-4 w-4 accent-black focus:ring-black border-gray-300 rounded"
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label
                                        htmlFor="confirmMedicalField"
                                        className="font-medium text-gray-700"
                                    >
                                        I confirm that I am within the medical
                                        field, either as a medical student at a
                                        registered medical school or as a
                                        registered medical practitioner. *
                                    </label>
                                </div>
                            </div>
                            {errors.confirmMedicalField && (
                                <p className="mt-1 text-sm text-red-600">
                                    {errors.confirmMedicalField.message}
                                </p>
                            )}

                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input
                                        {...register("receiveJobAlerts")}
                                        id="receiveJobAlerts"
                                        type="checkbox"
                                        className="h-4 w-4 accent-black focus:ring-black border-gray-300 rounded"
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label
                                        htmlFor="receiveJobAlerts"
                                        className="font-medium text-gray-700"
                                    >
                                        Keep me up to date with new job
                                        opportunities
                                    </label>
                                </div>
                            </div>
                        </div>

                        <TurnstileWidget ref={turnstileRef} />

                        <div className="flex justify-center mt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex justify-center py-2 px-8 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50"
                            >
                                {loading ? "Creating account..." : "Sign up"}
                            </button>
                        </div>
                        <p className="text-center text-sm text-gray-600 mt-4">
                            Already have an account?{" "}
                            <button
                                type="button"
                                onClick={() => navigate("/login")}
                                className="font-semibold text-gray-900"
                            >
                                Login
                            </button>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SignUpPage;
